import { useMemo } from "react";

import clsx from "clsx";
import { cloneDeep, uniqBy } from "lodash";

import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import {
  useUniversalBenchmarksRow,
  type BenchmarkIndicatorRow,
} from "@features/universal-benchmarks";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  makeNewCac,
  makeNewCacFt,
  makeNewCacRtn,
  makeRev,
  makeRevFt,
  makeRevRtn,
  makeRoas,
  makeRoasFt,
  makeRoasRtn,
  makeTxns,
  makeTxnsFt,
  makeTxnsRtn,
  type SalesMetricV2,
} from "@north-beam/nb-client-side/metrics-v2";
import { formatting } from "@north-beam/nb-common";
import { type CellContext } from "@tanstack/react-table";

import { type SalesMetricRow } from "../../../../../nb-common/src/metrics-v2";
import { useTableStateDimensions } from "../hooks/use-table-state-dimensions";
import { type TableState } from "../types";
import { selectedTabToLevel } from "../utils";

import { CellAovFormatter } from "./cell-aov-formatter";
import { CellAvgTouchpointsPerOrderFormatter } from "./cell-avg-touchpoints-per-order-formatter";
import { CellCustomersFormatter } from "./cell-customers-formatter";
import { CellMetricWithComparisonFormatter } from "./cell-metric-with-comparison-formatter";
import { CellRevenueFormatter } from "./cell-revenue-formatter";
import { CellRoasFormatter } from "./cell-roas-formatter";
import { CellVisitorsFormatter } from "./cell-visitors-formatter";

export const CellSalesContentCell = ({
  cell,
  metric,
  tableState,
}: {
  cell: CellContext<SalesMetricRow, unknown>;
  metric: SalesMetricV2;
  tableState: TableState;
}) => {
  const { user } = useUser();
  const { breakdownDimension, nameColumn } =
    useTableStateDimensions(tableState);
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();

  const rowItemName = cell.row.getIsPinned()
    ? "Grand Total"
    : nameColumn.getValue(cell.row.original) ?? "";

  const formattedValue = formatting.formatMetric(
    metric.getValue(cell.row.original.metrics),
    metric.format,
    { precision: "exact", code: user.currencies.currentCurrency },
  );

  const benchmarkBreakdowns = useMemo(() => {
    if (cell.row.getIsPinned()) {
      return [];
    }

    if (breakdownDimension.id === "date") {
      return [];
    }

    return [
      {
        [breakdownDimension.name]:
          breakdownDimension.getValue(cell.row.original) ?? "",
      },
    ];
  }, [cell.row, breakdownDimension]);

  const breakdownFilters = useMemo(() => {
    const filters = cloneDeep(tableState.breakdownFilters);

    if (
      cell.row.getIsPinned() &&
      tableState.selectedTab !== "breakdown" &&
      breakdownDimension.id !== "date"
    ) {
      for (const selectionRow of tableState.breakdownSelection) {
        const breakdown = breakdownDimension.name;
        const value = selectionRow.label;

        filters.push({ breakdown, value });
      }
    }

    return uniqBy(filters, (e) => `${e.breakdown}${e.value}`);
  }, [
    cell.row,
    breakdownDimension,
    tableState.breakdownFilters,
    tableState.breakdownSelection,
  ]);

  const universalBenchmarks = useUniversalBenchmarksRow({
    level: selectedTabToLevel(tableState.selectedTab),
    attributionModel,
    attributionWindow,
    accountingMode,
    breakdowns: benchmarkBreakdowns,
    filters: breakdownFilters,
  });

  const benchmarkKpis = useMemo(() => {
    if (!universalBenchmarks || universalBenchmarks.status === "loading") {
      return null;
    }

    const rv: BenchmarkIndicatorRow[] = [];

    universalBenchmarks.benchmarks.forEach((benchmark) => {
      rv.push({
        metricId: benchmark.metricV2.id,
        name: benchmark.metricV2.name,
        current: benchmark.metricV2.getValue(cell.row.original.metrics) ?? 0,
        target: benchmark.value,
        format: benchmark.metricV2.format,
        positiveTrend: benchmark.metricV2.positiveTrend,
      });
    });

    return rv;
  }, [cell.row.original.metrics, universalBenchmarks]);

  if (
    universalBenchmarks?.status === "loading" &&
    universalBenchmarks?.adoptedMetricIds?.some((id) => id === cell.column.id)
  ) {
    return (
      <div
        className={clsx(
          "border-r-1 border-x-v2-tertiary px-4 bg-inherit",
          "flex items-center justify-end h-full",
        )}
      >
        <Spinner className="w-3 h-3 text-v2-cosmic/400 text-2xs" />
      </div>
    );
  }

  if (["rev", "revFt", "revRtn"].includes(cell.column.id)) {
    return (
      <CellRevenueFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
        rowItemName={rowItemName}
        tooltipMetrics={{
          metricBlended: makeRev({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricFt: makeRevFt({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricRtn: makeRevRtn({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
        }}
      />
    );
  }

  if (["txns", "txnsFt", "txnsRtn"].includes(cell.column.id)) {
    return (
      <CellRevenueFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
        rowItemName={rowItemName}
        tooltipMetrics={{
          metricBlended: makeTxns({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricFt: makeTxnsFt({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricRtn: makeTxnsRtn({
            attributionModelLabel:
              user.attributionMethodEnum.getLabel(attributionModel),
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
        }}
      />
    );
  }

  if (["avgTouchpointsPerOrder"].includes(cell.column.id)) {
    return (
      <CellAvgTouchpointsPerOrderFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
      />
    );
  }

  if (["visitors"].includes(cell.column.id)) {
    return (
      <CellVisitorsFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
      />
    );
  }

  if (["customers"].includes(cell.column.id)) {
    return (
      <CellCustomersFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
      />
    );
  }

  if (["aov", "aovFt", "aovRtn"].includes(cell.column.id)) {
    return (
      <CellAovFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
        rowItemName={rowItemName}
      />
    );
  }

  if (["newCac", "newCacFt", "newCacRtn"].includes(cell.column.id)) {
    return (
      <CellRoasFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
        rowItemName={rowItemName}
        benchmarkKpis={benchmarkKpis}
        tooltipTableMetrics={{
          metricBlended: makeNewCac({ accountingMode, attributionWindow }),
          metricFt: makeNewCacFt({ accountingMode, attributionWindow }),
          metricRtn: makeNewCacRtn({ accountingMode, attributionWindow }),
        }}
      />
    );
  }

  if (["roas", "roasFt", "roasRtn"].includes(cell.column.id)) {
    return (
      <CellRoasFormatter
        value={formattedValue}
        cell={cell}
        tableState={tableState}
        metric={metric}
        rowItemName={rowItemName}
        benchmarkKpis={benchmarkKpis}
        tooltipTableMetrics={{
          metricBlended: makeRoas({
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricFt: makeRoasFt({
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
          metricRtn: makeRoasRtn({
            accountingMode,
            attributionWindow,
            isDashboardV2: true,
          }),
        }}
      />
    );
  }

  return (
    <CellMetricWithComparisonFormatter
      showPercentChange={tableState.showPercentChange}
      metric={metric}
      formattedValue={formattedValue}
      cell={cell}
      benchmarkKpi={
        benchmarkKpis?.find((e) => e.metricId === metric.id) ?? null
      }
      benchmarkItemName={rowItemName}
    />
  );
};
